import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { Alert, Button, Card, Field, Input, Modal, Select } from './ui';

/**
 * Groups of encoders and flows on the Encoders page (server:
 * services/resource-groups.ts). A group holds only the list; start, stop and
 * delete go to each member's own route, so each keeps its own checks. Order
 * matters: flows start before encoders (an encoder may ride on one), and
 * encoders stop and go before flows.
 */

export type MemberKind = 'encoder' | 'flow';
export interface Member { kind: MemberKind; id: string }
export interface ResourceGroup { id: string; label: string; createdAt: string; members: Member[]; hidden: number }

/** What the page knows about one encoder or flow. */
export interface Resource {
  kind: MemberKind; id: string; name: string; state: string;
  progress: string | null; error: string | null; busy: boolean;
}

export const keyOf = (m: Member) => `${m.kind}:${m.id}`;
export const memberOf = (key: string): Member => {
  const [kind, id] = key.split(/:(.*)/s);
  return { kind: kind as MemberKind, id };
};

const TRANSITIONAL = ['creating', 'starting', 'stopping', 'deleting', 'updating'];
const isRunning = (r: Resource) => r.state === 'running';
const isFailed = (r: Resource) => r.state === 'failed' || !!r.error;
const isMoving = (r: Resource) => r.busy || TRANSITIONAL.includes(r.state);

/**
 * A row's tick box: its own full-height target beside the row's link, so a
 * tick never opens the encoder or flow.
 */
export function SelectBox({ checked, onToggle, label }: { checked: boolean; onToggle: () => void; label: string }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={`Select ${label}`}
      onClick={onToggle}
      className="flex shrink-0 cursor-pointer items-center self-stretch pl-4 pr-1"
    >
      <span className={`flex h-4 w-4 items-center justify-center rounded border text-[10px] leading-none ${checked ? 'border-series-1 bg-series-1 text-white' : 'border-ink-muted bg-surface'}`}>
        {checked ? '✓' : ''}
      </span>
    </button>
  );
}

/** A list row: the tick box (when given) and the link to the resource. */
export function SelectableRow({ box, to, children }: { box?: ReactNode; to: string; children: ReactNode }) {
  return (
    <div className="flex items-stretch hover:bg-raised/40">
      {box}
      <Link to={to} className="flex min-w-0 flex-1 flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3">{children}</Link>
    </div>
  );
}

function Dot({ colour, pulse }: { colour: string; pulse?: boolean }) {
  return <span className={`h-2 w-2 shrink-0 rounded-full ${pulse ? 'animate-pulse-soft' : ''}`} style={{ background: colour }} />;
}

function stateColour(s: string) {
  return s === 'running' ? 'var(--color-good)'
    : s === 'failed' ? 'var(--color-critical)'
    : TRANSITIONAL.includes(s) ? 'var(--color-warning)'
    : 'var(--color-ink-muted)';
}

/** The group as one line: all running, some, none; anything moving; any fault. */
function Summary({ rs }: { rs: Resource[] }) {
  if (!rs.length) return <span className="text-xs text-ink-muted">empty</span>;
  const on = rs.filter(isRunning).length;
  const moving = rs.filter(isMoving).length;
  const failed = rs.filter(isFailed).length;
  const [colour, word] =
    failed ? ['var(--color-critical)', `${failed} with errors`]
    : moving ? ['var(--color-warning)', `${moving} changing`]
    : on === rs.length ? ['var(--color-good)', 'all running']
    : on === 0 ? ['var(--color-ink-muted)', 'none running']
    : ['var(--color-warning)', 'some running'];
  return (
    <span className="inline-flex items-center gap-1.5 text-xs">
      <Dot colour={colour} pulse={moving > 0} />
      <span className="text-ink-secondary">{word} · {on} of {rs.length} running</span>
    </span>
  );
}

type Act = 'start' | 'stop' | 'delete';

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Polls one resource until `done` says so, or gives up after `ms`. */
async function waitFor(m: Member, done: (state: string | null) => boolean, ms: number) {
  const until = Date.now() + ms;
  while (Date.now() < until) {
    const state = await api.get<{ encoder?: { state: string }; flow?: { state: string } }>(m.kind === 'encoder' ? `/encoders/${m.id}` : `/flows/${m.id}`)
      .then((r) => (r.encoder ?? r.flow)?.state ?? null)
      .catch((err) => (err instanceof ApiError && err.status === 404 ? null : 'unknown'));
    if (done(state)) return;
    await wait(5000);
  }
}

/** Runs one action over a group's members in the right order; returns what failed. */
async function runOver(act: Act, rs: Resource[], onStep: (s: string) => void): Promise<string[]> {
  const failures: string[] = [];
  const path = (r: Resource) => (r.kind === 'encoder' ? `/encoders/${r.id}` : `/flows/${r.id}`);
  const each = async (list: Resource[], call: (r: Resource) => Promise<unknown>) => {
    await Promise.all(list.map((r) => call(r).catch((err) => {
      failures.push(`${r.name}: ${err instanceof ApiError ? err.message : 'failed'}`);
    })));
  };
  const flows = rs.filter((r) => r.kind === 'flow');
  const encoders = rs.filter((r) => r.kind === 'encoder');

  if (act === 'start') {
    const fl = flows.filter((r) => !isRunning(r) && !isMoving(r));
    onStep(`Starting ${fl.length} flow(s)`);
    await each(fl, (r) => api.post(`${path(r)}/start`, {}));
    if (fl.length && encoders.length) {
      onStep('Waiting for the flows to run before starting encoders');
      await Promise.all(fl.map((r) => waitFor(r, (s) => s !== 'starting', 5 * 60_000)));
    }
    const en = encoders.filter((r) => !isRunning(r) && !isMoving(r));
    onStep(`Starting ${en.length} encoder(s)`);
    await each(en, (r) => api.post(`${path(r)}/start`, {}));
  } else if (act === 'stop') {
    const en = encoders.filter((r) => isRunning(r) && !r.busy);
    onStep(`Stopping ${en.length} encoder(s)`);
    await each(en, (r) => api.post(`${path(r)}/stop`, {}));
    const fl = flows.filter((r) => isRunning(r) && !r.busy);
    onStep(`Stopping ${fl.length} flow(s)`);
    await each(fl, (r) => api.post(`${path(r)}/stop`, {}));
  } else {
    onStep(`Deleting ${encoders.length} encoder(s)`);
    await each(encoders, (r) => api.delete(path(r), { confirmName: r.name }));
    if (encoders.length && flows.length) {
      // A flow an encoder rides on cannot go while the encoder is still there.
      onStep('Waiting for the encoders to go before deleting flows');
      await Promise.all(encoders.map((r) => waitFor(r, (s) => s === null || s === 'deleted' || s === 'failed', 10 * 60_000)));
    }
    onStep(`Deleting ${flows.length} flow(s)`);
    await each(flows, (r) => api.delete(path(r), { confirmName: r.name }));
  }
  return failures;
}

export function ResourceGroups({ groups, resources, selected, onToggle, onClearSelection, canEdit, canStart, canStop, canDelete, reload }: {
  groups: ResourceGroup[];
  resources: Map<string, Resource>;
  selected: Set<string>;
  onToggle: (key: string) => void;
  onClearSelection: () => void;
  canEdit: boolean; canStart: boolean; canStop: boolean; canDelete: boolean;
  reload: () => void;
}) {
  const [creating, setCreating] = useState(false);
  const [label, setLabel] = useState('');
  const [addTo, setAddTo] = useState('');
  const [renaming, setRenaming] = useState<ResourceGroup | null>(null);
  const [abandoning, setAbandoning] = useState<ResourceGroup | null>(null);
  const [deleting, setDeleting] = useState<ResourceGroup | null>(null);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const picked = [...selected].map(memberOf);
  const groupOf = new Map<string, string>();
  for (const g of groups) for (const m of g.members) groupOf.set(keyOf(m), g.id);
  const membersOf = (g: ResourceGroup) => g.members.map((m) => resources.get(keyOf(m))).filter((r): r is Resource => !!r);

  const save = async (fn: () => Promise<unknown>, after?: () => void) => {
    setSaving(true); setError(null);
    try { await fn(); after?.(); onClearSelection(); }
    catch (err) { setError(err instanceof ApiError ? err.message : 'That did not work.'); }
    finally { setSaving(false); reload(); }
  };

  const act = async (g: ResourceGroup, a: Act) => {
    setBusy((b) => ({ ...b, [g.id]: 'Working' })); setError(null);
    try {
      const failures = await runOver(a, membersOf(g), (s) => setBusy((b) => ({ ...b, [g.id]: s })));
      if (a === 'delete' && !failures.length && g.hidden === 0) await api.delete(`/resource-groups/${g.id}`);
      if (failures.length) setError(`${g.label}: ${failures.join(' · ')}`);
    } finally {
      setBusy((b) => { const { [g.id]: _, ...rest } = b; return rest; });
      reload();
    }
  };

  const selectionBar: ReactNode = picked.length > 0 && canEdit && (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-series-1/40 bg-series-1/5 px-3 py-2 text-sm">
      <span className="font-medium">{picked.length} selected</span>
      <Button variant="primary" onClick={() => { setLabel(''); setCreating(true); }}>Create group…</Button>
      {groups.length > 0 && (
        <>
<div className="w-56">
          <Select value={addTo} onChange={(ev) => setAddTo(ev.target.value)} aria-label="Group to add to">
              <option value="">Add to group…</option>
              {groups.map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}
            </Select>
          </div>
          <Button variant="secondary" disabled={!addTo || saving} onClick={() => void save(() => api.post(`/resource-groups/${addTo}/members`, { members: picked }), () => setAddTo(''))}>Add</Button>
        </>
      )}
      {picked.some((m) => groupOf.has(keyOf(m))) && (
        <Button
          variant="ghost"
          disabled={saving}
          onClick={() => void save(async () => {
            const byGroup = new Map<string, Member[]>();
            for (const m of picked) {
              const gid = groupOf.get(keyOf(m));
              if (gid) byGroup.set(gid, [...(byGroup.get(gid) ?? []), m]);
            }
            for (const [gid, members] of byGroup) await api.delete(`/resource-groups/${gid}/members`, { members });
          })}
        >
          Remove from group
        </Button>
      )}
      <Button variant="ghost" onClick={onClearSelection}>Clear</Button>
    </div>
  );

  return (
    <div className="space-y-3">
      {selectionBar}
      {error && <Alert tone="critical">{error}</Alert>}

      {groups.map((g) => {
        const rs = membersOf(g);
        const working = busy[g.id];
        const anyMoving = rs.some(isMoving) || !!working;
        return (
          <Card
            key={g.id}
            title={
              <span className="flex flex-wrap items-center gap-3">
                <span>{g.label}</span>
                <Summary rs={rs} />
                {working && <span className="text-xs text-ink-muted">{working}…</span>}
              </span>
            }
            action={
              <div className="flex flex-wrap gap-2">
                {canStart && <Button variant="primary" disabled={anyMoving || !rs.some((r) => !isRunning(r))} onClick={() => void act(g, 'start')}>Start all</Button>}
                {canStop && <Button variant="secondary" disabled={anyMoving || !rs.some(isRunning)} onClick={() => void act(g, 'stop')}>Stop all</Button>}
                {canEdit && <Button variant="ghost" onClick={() => { setLabel(g.label); setRenaming(g); }}>Rename</Button>}
                {canEdit && <Button variant="ghost" onClick={() => setAbandoning(g)}>Abandon group</Button>}
                {canDelete && <Button variant="danger" disabled={anyMoving} onClick={() => { setTyped(''); setDeleting(g); }}>Delete all…</Button>}
              </div>
            }
          >
            <div className="divide-y divide-grid">
              {rs.length === 0 && <div className="px-4 py-3 text-sm text-ink-muted">Nothing in this group. Tick encoders or flows below and add them.</div>}
              {rs.map((r) => (
                <SelectableRow
                  key={keyOf(r)}
                  to={r.kind === 'encoder' ? `/encoders/${r.id}` : `/flows/${r.id}`}
                  box={canEdit && <SelectBox checked={selected.has(keyOf(r))} onToggle={() => onToggle(keyOf(r))} label={r.name} />}
                >
                  <div className="min-w-0 flex-1">
                    <span className="font-medium">{r.name}</span>
                    <span className="ml-2 text-xs text-ink-muted">{r.kind}</span>
                    {r.error && <div className="text-xs text-critical">{r.error}</div>}
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-xs">
                    <Dot colour={stateColour(r.state)} pulse={isMoving(r)} />
                    <span className="text-ink-secondary">{r.state}{isMoving(r) && r.progress ? ` — ${r.progress}` : ''}</span>
                  </span>
                </SelectableRow>
              ))}
              {g.hidden > 0 && <div className="px-4 py-2 text-xs text-ink-muted">+ {g.hidden} you do not have access to</div>}
            </div>
          </Card>
        );
      })}

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title={`Group ${picked.length} resource${picked.length === 1 ? '' : 's'}`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setCreating(false)}>Cancel</Button>
            <Button variant="primary" loading={saving} disabled={!label.trim()} onClick={() => void save(() => api.post('/resource-groups', { label, members: picked }), () => setCreating(false))}>Create group</Button>
          </>
        }
      >
        <Field label="Label" hint="Anything already in another group moves to this one.">
          <Input value={label} onChange={(ev) => setLabel(ev.target.value)} maxLength={60} autoFocus placeholder="e.g. NZC v India, 26 Oct" />
        </Field>
      </Modal>

      <Modal
        open={!!renaming}
        onClose={() => setRenaming(null)}
        title="Rename group"
        footer={
          <>
            <Button variant="ghost" onClick={() => setRenaming(null)}>Cancel</Button>
            <Button variant="primary" loading={saving} disabled={!label.trim()} onClick={() => void save(() => api.patch(`/resource-groups/${renaming!.id}`, { label }), () => setRenaming(null))}>Save</Button>
          </>
        }
      >
        <Field label="Label"><Input value={label} onChange={(ev) => setLabel(ev.target.value)} maxLength={60} autoFocus /></Field>
      </Modal>

      <Modal
        open={!!abandoning}
        onClose={() => setAbandoning(null)}
        title={`Abandon ${abandoning?.label ?? ''}?`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setAbandoning(null)}>Cancel</Button>
            <Button variant="primary" loading={saving} onClick={() => void save(() => api.delete(`/resource-groups/${abandoning!.id}`), () => setAbandoning(null))}>Abandon group</Button>
          </>
        }
      >
        <p className="text-sm">The group goes. Every encoder and flow in it stays exactly as it is, running or not, back in the lists below.</p>
      </Modal>

      <Modal
        open={!!deleting}
        onClose={() => setDeleting(null)}
        title={`Delete everything in ${deleting?.label ?? ''}?`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleting(null)}>Cancel</Button>
            <Button
              variant="danger"
              disabled={typed.trim() !== deleting?.label}
              onClick={() => { const g = deleting!; setDeleting(null); void act(g, 'delete'); }}
            >
              Delete all and the group
            </Button>
          </>
        }
      >
        {deleting && (
          <div className="space-y-3 text-sm">
            <p>These are deleted for good, with their AWS resources, receivers and addresses. Running ones go off air.</p>
            <ul className="list-disc pl-5">
              {membersOf(deleting).map((r) => <li key={keyOf(r)}>{r.name} <span className="text-ink-muted">({r.kind}, {r.state})</span></li>)}
            </ul>
            {deleting.hidden > 0 && <p className="text-ink-muted">{deleting.hidden} more in the group you cannot see are left alone; the group stays for them.</p>}
            <Field label={`Type ${deleting.label} to confirm`}>
              <Input value={typed} onChange={(ev) => setTyped(ev.target.value)} autoFocus />
            </Field>
          </div>
        )}
      </Modal>
    </div>
  );
}
